import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { TimerIcon } from '../../pages/icons';
import { setTimerSeconds, selectTimerSeconds } from '../../core/store/app';
import * as S from './styled';

export const useInitTimer = (initialMinutes: number) => {
  const dispatch = useDispatch();
  const stateSeconds = useSelector(selectTimerSeconds);
  const initValue = stateSeconds ?? initialMinutes * 60;
  const [totalSeconds, setTotalSeconds] = useState<number>(initValue);
  const lastStateValue = useRef(stateSeconds);

  useEffect(() => {
    if (stateSeconds !== null && stateSeconds !== lastStateValue.current) {
      lastStateValue.current = stateSeconds;
      setTotalSeconds(stateSeconds);
    }
  }, [stateSeconds]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTotalSeconds((prevTotalSeconds) => {
        if (prevTotalSeconds > 0) {
          return prevTotalSeconds - 1;
        }
        return 0;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    dispatch(setTimerSeconds(totalSeconds));
  }, [totalSeconds, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setTimerSeconds(null));
    };
  }, [dispatch]);
};

export const useTimer = () => {
  const totalSeconds = useSelector(selectTimerSeconds) || 0;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return { minutes, seconds };
};

export const useResetTimer = (initialMinutes: number, resetTimer: boolean) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (resetTimer) {
      dispatch(setTimerSeconds(initialMinutes * 60));
    }
  }, [dispatch, initialMinutes, resetTimer]);
};

export const checkNumber = (value: number | string) => {
  value = value.toString();
  if (value.length === 1) {
    return `0${value}`;
  }
  return value;
};

export const Timer = ({
  initialMinutes = 5,
  resetTimer = true,
  hideTimer = false,
}: {
  initialMinutes?: number;
  resetTimer?: boolean;
  hideTimer?: boolean;
}) => {
  useResetTimer(initialMinutes, resetTimer);
  useInitTimer(initialMinutes);
  const { minutes, seconds } = useTimer();
  const { t } = useTranslation();

  return (
    <S.TimerTime $hideTimer={hideTimer}>
      <TimerIcon />
      <span
        dangerouslySetInnerHTML={{
          __html: t('offer_ends', {
            time: `${minutes}:${checkNumber(seconds)}`,
          }),
        }}
      />
    </S.TimerTime>
  );
};

export const VerticalTimer = ({
  initialMinutes = 5,
  resetTimer = true,
}: {
  initialMinutes?: number;
  resetTimer?: boolean;
}) => {
  useResetTimer(initialMinutes, resetTimer);
  useInitTimer(initialMinutes);
  const { minutes, seconds } = useTimer();
  const { t } = useTranslation();

  return (
    <S.VerticalTimer>
      <S.TimerColumn>
        <S.TimerCount>{minutes}</S.TimerCount>
        <span>{t('minutes')}</span>
      </S.TimerColumn>
      <S.TimerColumn $start>:</S.TimerColumn>
      <S.TimerColumn>
        <S.TimerCount>{checkNumber(seconds)}</S.TimerCount>
        <span>{t('seconds')}</span>
      </S.TimerColumn>
    </S.VerticalTimer>
  );
};
